import { Popover } from 'radix-ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { Checkbox } from './Checkbox';
import { GLYPH } from './glyphs';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  value: readonly string[];
  onValueChange: (value: string[]) => void;
  options: readonly MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Две колонки в списке (README: multi с длинным справочником). */
  twoColumns?: boolean;
  id?: string;
  className?: string;
  onBlur?: () => void;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
}

/**
 * Multi — выпадающий список с чекбоксами (README «Поля форм»): триггер 34 / radius 16,
 * сводка выбранного через « · », подвал «N выбрано» и «Готово».
 */
export function MultiSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  invalid,
  twoColumns = false,
  id,
  className,
  onBlur,
  ...aria
}: MultiSelectProps) {
  const { t } = useTranslation('form');
  const listId = useId();
  const selected = options.filter((option) => value.includes(option.value));
  const summary = selected.map((option) => option.label).join(` ${GLYPH.middot} `);

  function toggle(optionValue: string) {
    const next = value.includes(optionValue)
      ? value.filter((item) => item !== optionValue)
      : options
          .map((option) => option.value)
          .filter((item) => item === optionValue || value.includes(item));
    onValueChange(next);
  }

  return (
    <Popover.Root onOpenChange={(open) => (open ? undefined : onBlur?.())}>
      <Popover.Trigger
        id={id}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'flex h-34 w-full min-w-0 cursor-pointer items-center gap-8 rounded-16 border border-line-field bg-paper-pure px-10 text-left',
          'outline-none hover:border-line-strong focus-visible:border-wine-bordeaux focus-visible:shadow-focus-field focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:bg-surface-sunken',
          'aria-invalid:shadow-[inset_0_0_0_1px_var(--vw-status-error)]',
          className,
        )}
        {...aria}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-[12.5px]',
            summary ? 'text-ink-900' : 'text-ink-400',
          )}
        >
          {summary || placeholder || t('multi.placeholder')}
        </span>
        <span aria-hidden="true" className="shrink-0 text-[8px] text-ink-500">
          {GLYPH.caretDown}
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={2}
          className="z-60 w-(--radix-popover-trigger-width) overflow-hidden rounded-16 border border-line-strong bg-paper-pure shadow-dropdown animate-rise-menu"
        >
          <fieldset
            id={listId}
            aria-labelledby={aria['aria-labelledby']}
            className={cn(
              'grid max-h-196 min-w-0 overflow-y-auto',
              twoColumns ? 'grid-cols-[repeat(auto-fit,minmax(200px,1fr))]' : 'grid-cols-1',
            )}
          >
            {options.map((option) => {
              const checked = value.includes(option.value);
              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-8 border-b border-line-soft px-10 py-6 hover:bg-surface-sunken has-focus-visible:bg-surface-sunken"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(option.value)}
                    className="focus-visible:outline-none"
                  />
                  <span
                    className={cn(
                      'min-w-0 truncate text-[12.5px]',
                      checked ? 'font-medium text-ink-900' : 'font-normal text-ink-700',
                    )}
                  >
                    {option.label}
                  </span>
                </label>
              );
            })}
          </fieldset>
          <div className="flex items-center gap-10 border-t border-line-hair bg-surface-sunken px-10 py-6">
            <span className="text-[11px] text-ink-500">
              {t('multi.selected', { count: selected.length })}
            </span>
            <span className="flex-1" />
            <Popover.Close className="cursor-pointer text-[11px] font-medium text-wine-bordeaux hover:text-accent-deep">
              {t('multi.done')}
            </Popover.Close>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
